import { forwardRef, useEffect, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { useSimpleTypesense } from '../hooks/useSimpleTypesense';
import { ReactSelectProps } from '../utils/types';
import { ReactSelect } from './ReactSelect';
import { Option } from './SimpleSelect';

export type TypesenseSelectProps = ReactSelectProps & {
  collection: string;
  queryBy: string;
  name: string;
  documentLabel: string;
  documentValue: string;
  suffixWithValue?: boolean;
  onChange: (event) => void;
  hasBlank?: boolean;
};

export const TypesenseSelect = forwardRef(
  ({
    collection,
    queryBy,
    name,
    documentLabel,
    documentValue,
    suffixWithValue = false,
    onChange,
    hasBlank = false,
    ...rest
  }: TypesenseSelectProps) => {
    const { results, setSearch, currentSearch } = useSimpleTypesense(collection, queryBy);
    const context = useFormContext();
    const value = useWatch({
      control: context.control,
      name,
    });
    const [defaultValue, setDefaultValue] = useState({
      value,
      label: value,
    });

    useEffect(() => {
      // Not searching
      if (currentSearch !== '' && value != currentSearch) {
        return;
      }
      setSearch(value);
      setDefaultValue({
        value,
        // YES, I know it's a `==` rather than an `===`. File a complaint with Typesense for not treating numbers as first-class citizens
        label: results.find((result) => result[documentValue] == value)?.[documentLabel] ?? value,
      });
    }, [currentSearch, documentLabel, documentValue, results, setSearch, value]);

    const options: Option[] = useMemo(
      () =>
        results?.map((result) => {
          const idAndLabel = `${result?.[documentLabel]} (${result?.[documentValue]})`;
          const label = result?.[documentLabel];
          const name_label = result?.[documentLabel];
          const value = result?.[documentValue];
          return {
            label: suffixWithValue ? idAndLabel : label,
            name_label,
            value,
          };
        }),
      [results, documentLabel, documentValue, suffixWithValue],
    );

    return (
      <ReactSelect
        onChange={onChange}
        onInputChange={setSearch}
        name={name}
        options={[...(hasBlank ? [{ label: '-', value: '' } satisfies Option] : []), ...options]}
        value={defaultValue}
        {...rest}
      />
    );
  },
);
